import { createBrowserRouter, Navigate } from 'react-router';

import { RedireccionLegacy } from '@/app/RedireccionLegacy';
import { PermissionGate } from '@/auth/PermissionGate';
import { RedirectIfAuthenticated, RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/components/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { RecuperarPage } from '@/features/auth/RecuperarPage';
import { RegistroPage } from '@/features/auth/RegistroPage';
import { RestablecerPage } from '@/features/auth/RestablecerPage';
import { VerificacionPage } from '@/features/auth/VerificacionPage';
import {
  BibliotecaPage,
  FlashcardsEvaluadasPage,
  FlashcardsPage,
  GuiasGpcPage,
  PresentacionesPage,
  ResumenesGpcPage,
  VideoTipsPage,
} from '@/features/biblioteca/BibliotecaPage';
import {
  FlashcardPage,
  PresentacionPage,
  ResumenGpcPage,
  VideoTipPage,
} from '@/features/biblioteca/VisoresBiblioteca';
import { ClasePage } from '@/features/curso/ClasePage';
import { ClasesPage } from '@/features/curso/ClasesPage';
import { CursoPage } from '@/features/curso/CursoPage';
import { AvancesPage } from '@/features/evaluaciones/AvancesPage';
import { EvaluacionPage } from '@/features/evaluaciones/EvaluacionPage';
import { ExamenesPage } from '@/features/evaluaciones/ExamenesPage';
import { ResultadosPage } from '@/features/evaluaciones/ResultadosPage';
import { SimuladorPage } from '@/features/evaluaciones/SimuladorPage';
import { BusquedaPage } from '@/features/busqueda/BusquedaPage';
import { EnarmPlusPage } from '@/features/manuales/EnarmPlusPage';
import { ManualRuta } from '@/features/manuales/ManualPage';
import { OrganizarPage } from '@/features/temario/OrganizarPage';
import { PlanPage } from '@/features/temario/PlanPage';
import { TemarioPage } from '@/features/temario/TemarioPage';
import { ConstanciasPage } from '@/features/cuenta/ConstanciasPage';
import { CuentaPage } from '@/features/cuenta/CuentaPage';
import { OpinionPage } from '@/features/cuenta/OpinionPage';
import { PuntajesPage } from '@/features/cuenta/PuntajesPage';
import { GrabacionesPage } from '@/features/live/GrabacionesPage';
import { InicioPage } from '@/features/inicio/InicioPage';
import { NoEncontradaPage } from '@/features/modulos/ModuloPage';
import { LicenciasPage } from '@/features/pagos/LicenciasPage';
import { PagarPage } from '@/features/pagos/PagarPage';
import { ResultadoPage } from '@/features/pagos/ResultadoPage';

export const router = createBrowserRouter([
  {
    element: <RedirectIfAuthenticated />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/verificacion', element: <VerificacionPage /> },
      { path: '/registro', element: <RegistroPage /> },
      { path: '/registro/verificar', element: <VerificacionPage /> },
      { path: '/recuperar-contrasenia', element: <RecuperarPage /> },
    ],
  },
  { path: '/restablecer/:token', element: <RestablecerPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/inicio', element: <InicioPage /> },
          { path: '/licencias', element: <LicenciasPage /> },
          { path: '/pagar/resultado', element: <ResultadoPage /> },
          { path: '/pagar/:membershipId', element: <PagarPage /> },
          // Fase 6: curso, clases, talleres y biblioteca. La biblioteca se filtra por las
          // bibliotecas de la licencia (como el legacy), no por sus switches.
          { path: '/curso-enarm', element: <CursoPage /> },
          { path: '/curso-enarm/clases', element: <ClasesPage tipo="class" /> },
          { path: '/curso-enarm/clases/:id/:pestana?', element: <ClasePage tipo="class" /> },
          {
            path: '/curso-enarm/talleres',
            element: (
              <PermissionGate permiso="workshop.access" modulo="Talleres">
                <ClasesPage tipo="workshop" />
              </PermissionGate>
            ),
          },
          {
            path: '/curso-enarm/talleres/:id/:pestana?',
            element: (
              <PermissionGate permiso="workshop.access" modulo="Talleres">
                <ClasePage tipo="workshop" />
              </PermissionGate>
            ),
          },
          // Fase 7: simulador (v2, D4), exámenes (prime, D16) y avances (sin QuizENARM, D17).
          { path: '/simulador', element: <SimuladorPage /> },
          { path: '/simulador/:id', element: <EvaluacionPage tipo="simulator" /> },
          { path: '/simulador/:id/resultados', element: <ResultadosPage tipo="simulator" /> },
          { path: '/curso-enarm/examenes-prime', element: <ExamenesPage /> },
          {
            path: '/curso-enarm/examenes-prime/intento/:id',
            element: <EvaluacionPage tipo="exam" />,
          },
          {
            path: '/curso-enarm/examenes-prime/intento/:id/resultados',
            element: <ResultadosPage tipo="exam" />,
          },
          { path: '/curso-enarm/avances', element: <AvancesPage inicial="curso" modulo /> },
          { path: '/avances', element: <AvancesPage inicial="simulador" /> },
          // Fase 8: manuales (un visor para las 5 variantes), ENARM Plus, temario y búsqueda.
          { path: '/manuales/enarm-plus', element: <EnarmPlusPage /> },
          { path: '/manuales/:slug', element: <ManualRuta /> },
          { path: '/curso-enarm/temario', element: <TemarioPage /> },
          { path: '/curso-enarm/temario/organizar', element: <OrganizarPage /> },
          { path: '/curso-enarm/temario/plan', element: <PlanPage /> },
          { path: '/busqueda', element: <BusquedaPage /> },
          // Fase 9: grabaciones, cuenta, constancias, opinión y puntajes (en vivo con Zoom: retirado).
          { path: '/live', element: <Navigate to="/live/grabaciones" replace /> },
          { path: '/live/grabaciones', element: <GrabacionesPage /> },
          { path: '/cuenta', element: <CuentaPage /> },
          { path: '/constancias', element: <ConstanciasPage /> },
          { path: '/opinion', element: <OpinionPage /> },
          { path: '/puntajes-enarm', element: <PuntajesPage /> },
          { path: '/curso-enarm/biblioteca', element: <BibliotecaPage /> },
          { path: '/curso-enarm/biblioteca/presentaciones', element: <PresentacionesPage /> },
          { path: '/curso-enarm/biblioteca/presentaciones/:id', element: <PresentacionPage /> },
          { path: '/curso-enarm/biblioteca/flashcards', element: <FlashcardsPage /> },
          { path: '/curso-enarm/biblioteca/flashcards/:id', element: <FlashcardPage /> },
          {
            path: '/curso-enarm/biblioteca/flashcards-evaluadas',
            element: <FlashcardsEvaluadasPage />,
          },
          { path: '/curso-enarm/biblioteca/video-tips', element: <VideoTipsPage /> },
          { path: '/curso-enarm/biblioteca/video-tips/:id', element: <VideoTipPage /> },
          {
            path: '/curso-enarm/biblioteca/guias-gpc',
            element: (
              <PermissionGate permiso="class.gpc_guides.access" modulo="Guías GPC">
                <GuiasGpcPage />
              </PermissionGate>
            ),
          },
          {
            path: '/curso-enarm/biblioteca/resumenes-gpc',
            element: (
              <PermissionGate
                permiso="library.gpc_summaries.access"
                modulo="Resúmenes de guías GPC"
              >
                <ResumenesGpcPage />
              </PermissionGate>
            ),
          },
          {
            path: '/curso-enarm/biblioteca/resumenes-gpc/:id',
            element: (
              <PermissionGate
                permiso="library.gpc_summaries.access"
                modulo="Resúmenes de guías GPC"
              >
                <ResumenGpcPage />
              </PermissionGate>
            ),
          },
          { path: '*', element: <NoEncontradaPage /> },
        ],
      },
    ],
  },
  { path: '/', element: <Navigate to="/inicio" replace /> },
  // Fase 11: URLs del PHP legacy (marcadores, correos y enlaces viejos) tras el corte.
  ...[
    '/inicio/*',
    '/legacy/*',
    '/legacy',
    '/enlace-de-recuperacion/*',
    '/logout',
    '/navegadores',
  ].map((path) => ({ path, element: <RedireccionLegacy /> })),
]);
